// Who a review comment is for, decided once.
//
// Every comment on a website, a document or a video is one of two things:
//
//   team    "Team only" — the studio talking to itself. Never sent to a client,
//           not on the share link, not in the client's portal, not on the
//           widget sitting on their site.
//   client  "Team + client" — the conversation with the client.
//
// The column that says so (`visibility`, migration 0078) does not exist until
// that migration is applied. Every comment written before it — and every
// comment read from a database that has not had it yet — was already being
// shown to the client, so a missing value means `client`. That is the honest
// reading of the past, and it is why nothing here treats `undefined` as private.
//
// The filter for a client's eyes lives here and nowhere else, and it is the
// one with tests in front of it: a reply is hidden when anything above it in
// its thread is team-only, so a client cannot read the answer to a question
// they were never shown.

export const TEAM = 'team'
export const CLIENT = 'client'

export const VISIBILITY_OPTIONS = [
  { key: TEAM, label: 'Team only' },
  { key: CLIENT, label: 'Team + client' },
]

/** The stored value, reduced to the two that mean something. Unknown is `client`. */
export function normalizeVisibility(value) {
  return String(value || '').trim().toLowerCase() === TEAM ? TEAM : CLIENT
}

export const isTeamOnly = (comment) => normalizeVisibility(comment?.visibility) === TEAM

export function visibilityLabel(value) {
  return normalizeVisibility(value) === TEAM ? 'Team only' : 'Team + client'
}

/** Left by the client (or a guest on a share link) rather than by the studio. */
export function isFromClient(comment) {
  const t = String(comment?.author_type || '').toLowerCase()
  return t === 'client' || t === 'guest'
}

/**
 * The comments a client may see.
 *
 * A comment is visible when it, and every comment above it in its thread, is
 * `client`. A parent that is missing from the list is treated as hidden: the
 * caller could not prove the thread was meant for the client, and a reply
 * shown out of its thread reads as a non sequitur at best and a leak at worst.
 */
export function visibleToClient(comments = []) {
  const rows = (comments || []).filter(Boolean)
  const byId = new Map(rows.map(c => [c.id, c]))
  const memo = new Map()

  const visible = (c, depth = 0) => {
    if (!c) return false
    if (memo.has(c.id)) return memo.get(c.id)
    // A cycle cannot happen in a parent_id tree, but a bad row must not hang
    // the page that is trying to decide whether to show it.
    if (depth > 50) return false
    let ok = !isTeamOnly(c)
    if (ok && c.parent_id) ok = visible(byId.get(c.parent_id), depth + 1)
    memo.set(c.id, ok)
    return ok
  }

  return rows.filter(c => visible(c))
}

/**
 * The panel's audience filter: everything, what came from the client, or the
 * team-only notes. Applied to thread roots; replies travel with their root.
 */
export function filterByAudience(comments = [], audience = 'all') {
  const rows = comments || []
  if (audience === 'client') return rows.filter(isFromClient)
  if (audience === 'team') return rows.filter(isTeamOnly)
  return rows
}

/**
 * What the composer may offer. Without the column a "Team only" note cannot
 * be kept from the client, so it is not offered as if it could be.
 */
export function composerVisibilityOptions(hasVisibilityColumn) {
  return VISIBILITY_OPTIONS.map(o => ({
    ...o,
    disabled: o.key === TEAM && !hasVisibilityColumn,
  }))
}

/**
 * The fields to write for a new comment's audience. Nothing at all when the
 * column is missing — writing it would fail the whole insert.
 */
export function visibilityFields(visibility, hasVisibilityColumn) {
  if (!hasVisibilityColumn) return {}
  return { visibility: normalizeVisibility(visibility) }
}
